// ============================================================================
// BehaviorDayOfWeek.tsx
// Section component for displaying behavior incidents by day of week
// Similar to Fall Analysis Day of Week section
// ============================================================================

import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useState } from "react";
import { Container, Child, Header, LayerContent } from "../styles";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import {
  barOptions,
  handlePointClicked,
} from "../formUtils";
import SectionModal from "./BehaviorAnalysisSectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { behaviorAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import useFormDataHelpers from "../../../../../hooks/useFormDataHelpers";
import { MITIGATE_COLORS } from "../../../../../shared/sharedConstants";

// ============================================================================
// Props Interface
// ============================================================================
interface BehaviorDayOfWeekProps {
  setClickedIncident: (item: any) => void;
  isFetchingSelectedIncident: boolean;
  shareTableData?: (data: any[]) => void;
}

// ============================================================================
// Row Type Definition
// ============================================================================
type DayOfWeekRow = {
  day: string;
  count: number;
  percentage: number;
};

// ============================================================================
// Component
// ============================================================================
const BehaviorDayOfWeek = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: BehaviorDayOfWeekProps) => {
  // ---------------------------------------------------------------------------
  // Helper Hooks
  // ---------------------------------------------------------------------------
  const { getFacilityLabel } = useFormDataHelpers();

  // ---------------------------------------------------------------------------
  // Local State
  // ---------------------------------------------------------------------------
  const [tableData, setTableData] = useState<DayOfWeekRow[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [modalData, setModalData] = useState<any>([]);
  const [dayOfWeek, setDayOfWeek] = useState("");
  const [showModal, setShowModal] = useState(false);

  // ---------------------------------------------------------------------------
  // Global State from Zustand
  // ---------------------------------------------------------------------------
  const { startDate, endDate, behaviorFacility, behaviorAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      behaviorFacility: state.behaviorFacility,
      behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
    }));

  // Derived value for facility name
  const facilityLabel = getFacilityLabel(behaviorFacility);

  // ---------------------------------------------------------------------------
  // Day order for consistent display
  // ---------------------------------------------------------------------------
  const DAY_ORDER = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // ---------------------------------------------------------------------------
  // Data Transformation Function - converts drilldown object to table rows
  // ---------------------------------------------------------------------------
  const adjustedTableData = (drilldownData: Record<string, any[]>): DayOfWeekRow[] => {
    if (!drilldownData || typeof drilldownData !== 'object') {
      return [];
    }

    let totalIncidentsSum = 0;
    const rows: DayOfWeekRow[] = [];

    // Process each day in order
    DAY_ORDER.forEach((day) => {
      const incidents = drilldownData[day] || [];
      const count = Array.isArray(incidents) ? incidents.length : 0;
      totalIncidentsSum += count;
      rows.push({
        day,
        count,
        percentage: 0, // Will calculate after we have total
      });
    });

    // Calculate percentages
    rows.forEach((row) => {
      row.percentage = totalIncidentsSum > 0 
        ? Math.round((row.count / totalIncidentsSum) * 100) 
        : 0;
    });

    // Add totals row
    rows.push({
      day: "Total",
      count: totalIncidentsSum,
      percentage: 100,
    });

    return rows;
  };

  // ---------------------------------------------------------------------------
  // Transform table data for bar chart (ensures chart order matches table order)
  // ---------------------------------------------------------------------------
  const transformTableDataForChart = (rows: DayOfWeekRow[]) => {
    // Filter out the totals row for the chart
    const dataRows = rows.filter(row => row.day !== "Total");
    
    if (dataRows.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = dataRows.map(row => row.day);
    const data = dataRows.map(row => row.count);

    // Alternating colors from MITIGATE_COLORS
    const alternatingColors = [
      MITIGATE_COLORS.BLUE[0],
      MITIGATE_COLORS.GREEN[0],
      MITIGATE_COLORS.RED[0],
      MITIGATE_COLORS.ORANGE[0],
      MITIGATE_COLORS.PURPLE[0],
      MITIGATE_COLORS.BLUE[1],
      MITIGATE_COLORS.GREEN[1],
    ];

    return {
      labels,
      datasets: [
        {
          label: "# of Incidents",
          data,
          backgroundColor: alternatingColors.slice(0, labels.length),
          borderWidth: 0,
        },
      ],
    };
  };

  // ---------------------------------------------------------------------------
  // Process data when behaviorAnalysisIncidents changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorAnalysisIncidents !== undefined) {
      const dayOfWeekSection = behaviorAnalysisIncidents?.dayOfWeekSection;
      // Use dayOfWeekDrilldown - object keyed by day name with arrays of incidents
      const drilldownData = dayOfWeekSection?.dayOfWeekDrilldown || {};
      
      const transformedData = adjustedTableData(drilldownData);
      const transformedBarData = transformTableDataForChart(transformedData);

      setTableData(transformedData);
      setBarData(transformedBarData);
      shareTableData?.(transformedData);
    }
  }, [behaviorAnalysisIncidents]);

  const title = "Day of Week Behavior Incident Analysis";

  // ---------------------------------------------------------------------------
  // Handle clicking a bar in the chart
  // ---------------------------------------------------------------------------
  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowModal(true);
    setDayOfWeek(label);
    // Get drilldown data for the clicked day from dayOfWeekSection
    const drilldownData = behaviorAnalysisIncidents?.dayOfWeekSection?.dayOfWeekDrilldown || {};
    const filteredDetails = drilldownData[label] || [];
    setModalData(filteredDetails);
  };

  // ---------------------------------------------------------------------------
  // Build the grid component when tableData changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };

    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="day" title="Day" />
          <GridColumn
            field="count"
            title="# of Incidents"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="40%">
          {grid}
        </Child>
        <Child flex="60%">
          <ChartContainer>
            {barData?.labels?.length > 0 ? (
              <Bar options={barOptions(handleBarClicked)} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>

      {showModal && (
        <SectionModal
          open={showModal}
          facilityName={facilityLabel}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {
            setDayOfWeek("");
            setModalData([]);
          }}
          isLoading={isFetchingSelectedIncident}
          tableData={modalData}
          tableCols={behaviorAnalysisDetailCols()}
          title={`${title} - ${dayOfWeek}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData,
              columnData: behaviorAnalysisDetailCols(),
              fileName: facilityLabel,
              startDate,
              endDate,
              subTitle: `${title} - ${dayOfWeek}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              modalData,
              behaviorAnalysisDetailCols(),
              `${facilityLabel} - ${title} - ${dayOfWeek}`
            )
          }
        />
      )}
    </LayerContent>
  );
};

export default BehaviorDayOfWeek;
